import {
  formatDate,
  unwrapSelect,
  type DateLike,
  type Selectable,
} from './buildOrderPayload';

export interface OrderEditItemFormValue {
  id?: string | null;
  product_id?: Selectable<string | null> | null;
  product_sku?: string | null;
  sku?: string | null;
  inventory_type_id?: Selectable<string | null> | null;
  inventory_id?: Selectable<string | null> | null;
  quantity?: number | null;
  manufacturing_date?: DateLike;
  expiration_date?: DateLike;
  batch?: string | null;
  note?: string | null;
  internal_note?: string | null;
  price?: number | null;
  identifier?: string | null;
  original_price?: number | null;
  cost?: number | null;
  custom_attributes?: Record<string, unknown> | null;
  bundle_id?: string | null;
  [extra: string]: unknown;
}

export interface OrderEditFormValues {
  order_type?: string | null;
  name?: string;
  extra_name?: string | null;
  sales_channel_id?: string | null;
  shipping_type_id?: string | null;
  country_code?: string | null;
  postal_code?: string | null;
  state?: string | null;
  city?: string | null;
  district?: string | null;
  address?: string;
  receiver_name?: string;
  phone?: string;
  email?: string | null;
  convenience_store_code?: string | null;
  total_amount?: number | string | null;
  currency?: string | null;
  is_cod?: boolean;
  delivery_with_pickup?: boolean;
  urgent?: boolean;
  scheduled_shipping_date?: DateLike;
  delivery_date?: DateLike;
  delivery_time?: string | null;
  seller_name?: string | null;
  seller_phone?: string | null;
  invoice_number?: string | null;
  note?: string | null;
  warehouse_id?: string | null;
  merchant_id?: string | null;
  order_items?: OrderEditItemFormValue[];
  [extra: string]: unknown;
}

export interface BuildOrderUpdatePayloadOptions {
  role: 'admin' | 'operator';
  /**
   * IDs of original order items whose product_id must be omitted from the
   * outgoing payload because the underlying record uses unknown_product_id
   * (the displayed product_id in the form is a stand-in UUID for the Select
   * widget — writing it back would corrupt the FK).
   */
  unknownProductItemIds: Set<string>;
}

export interface OrderItemUpdatePayload {
  id: string | null | undefined;
  product_id?: string | null;
  sku: string | null | undefined;
  inventory_type_id: string | null | undefined;
  quantity: number;
  manufacturing_date: string | null;
  expiration_date: string | null;
  batch: string | null | undefined;
  note: string | null | undefined;
  internal_note: string | null | undefined;
  price: number | null | undefined;
  identifier: string | null | undefined;
  original_price: number | null | undefined;
  cost: number | null | undefined;
  custom_attributes: Record<string, unknown> | null;
  inventory_id: string | null;
  bundle_id?: string | null;
}

export interface OrderUpdatePayload {
  order_type: string | null | undefined;
  order: {
    name: string | undefined;
    extra_name: string | null;
    sales_channel_id: string | null | undefined;
    shipping_type_id: string | null | undefined;
    country_code: string | null;
    postal_code: string | null;
    state: string | null;
    city: string | null;
    district: string | null;
    address: string | undefined;
    receiver_name: string | undefined;
    phone: string | undefined;
    email: string | null;
    convenience_store_code: string | null;
    total_amount: number | string | null;
    currency: string | null;
    is_cod: boolean | undefined;
    delivery_with_pickup: boolean | undefined;
    urgent: boolean | undefined;
    scheduled_shipping_date: string | null;
    delivery_date: string | null;
    delivery_time: string | null;
    seller_name: string | null;
    seller_phone: string | null;
    invoice_number: string | null;
    note: string | null;
    order_items: OrderItemUpdatePayload[];
    merchant_id?: string;
    warehouse_id?: string;
  };
}

// custom_attributes 從 form state 直接以 Hash 形態傳到後端（orders_controller
// permit `{ custom_attributes: {} }` 接得到 nested Hash）。空 Hash 在 DB 落 nil
// 由 OrderItem#normalize_custom_attributes 處理，FE 這層不再做 JSON.stringify。
function normalizeCustomAttributes(
  value: OrderEditItemFormValue['custom_attributes'],
): Record<string, unknown> | null {
  if (value && typeof value === 'object') return value;
  return null;
}

function mapUpdateItem(
  item: OrderEditItemFormValue,
  unknownProductItemIds: Set<string>,
): OrderItemUpdatePayload {
  const productId = unwrapSelect(item.product_id);
  const isUnknown = !!item.id && unknownProductItemIds.has(item.id);

  const payload: OrderItemUpdatePayload = {
    id: item.id,
    sku: item.product_sku || item.sku,
    inventory_type_id: unwrapSelect(item.inventory_type_id),
    quantity: item.quantity || 0,
    manufacturing_date: formatDate(item.manufacturing_date),
    expiration_date: formatDate(item.expiration_date),
    batch: item.batch,
    note: item.note,
    internal_note: item.internal_note,
    price: item.price,
    identifier: item.identifier,
    original_price: item.original_price,
    cost: item.cost,
    custom_attributes: normalizeCustomAttributes(item.custom_attributes),
    inventory_id: unwrapSelect(item.inventory_id) || null,
  };

  if (!isUnknown) {
    payload.product_id = productId;
  }

  // 新增品項才帶 bundle_id（無 id 表示新增）；既有品項的 bundle 身份已在 create
  // 時 materialise 為 OrderBundle，update 不需要重送，避免覆寫快照。
  if (!item.id) {
    payload.bundle_id = item.bundle_id || null;
  }

  return payload;
}

/**
 * Build the request body sent to `PATCH /orders/:id` from OrderEdit form values.
 *
 * Pure function — closure deps (`role`, `unknownProductItemIds`) are passed
 * explicitly so this can be unit-tested without mounting the component.
 *
 * Differences from `buildOrderPayload` (the create variant) that justify a
 * separate util:
 *   - wraps the body with an extra `order_type` field
 *   - each item carries its `id`; `bundle_id` is preserved for new items
 *     added via bundle expansion (no `id`), dropped for existing item updates
 *     (bundle snapshots already materialised when the item was first created)
 *   - excludes `product_id` for items whose original record uses
 *     `unknown_product_id` (see options.unknownProductItemIds)
 *   - operator merchant_id is read from form values, not from context
 */
export function buildOrderUpdatePayload(
  values: OrderEditFormValues,
  options: BuildOrderUpdatePayloadOptions,
): OrderUpdatePayload {
  const { role, unknownProductItemIds } = options;

  const mergedOrderItems = (values.order_items || []).map(item =>
    mapUpdateItem(item, unknownProductItemIds),
  );

  const payload: OrderUpdatePayload = {
    order_type: values.order_type,
    order: {
      name: values.name,
      extra_name: values.extra_name || null,
      sales_channel_id: values.sales_channel_id,
      shipping_type_id: values.shipping_type_id,
      country_code: values.country_code || null,
      postal_code: values.postal_code || null,
      state: values.state || null,
      city: values.city || null,
      district: values.district || null,
      address: values.address,
      receiver_name: values.receiver_name,
      phone: values.phone,
      email: values.email || null,
      convenience_store_code: values.convenience_store_code || null,
      // 空 / null / undefined 都 fallback 到 0；其他值（含 "0", "0.00", 數字）原樣送 BE。
      total_amount: values.total_amount == null || values.total_amount === '' ? 0 : values.total_amount,
      currency: values.currency || null,
      is_cod: values.is_cod,
      delivery_with_pickup: values.delivery_with_pickup,
      urgent: values.urgent,
      scheduled_shipping_date: formatDate(values.scheduled_shipping_date),
      delivery_date: formatDate(values.delivery_date),
      delivery_time: values.delivery_time || null,
      seller_name: values.seller_name || null,
      seller_phone: values.seller_phone || null,
      invoice_number: values.invoice_number || null,
      note: values.note || null,
      order_items: mergedOrderItems,
    },
  };

  if (role === 'operator' && values.merchant_id) {
    payload.order.merchant_id = values.merchant_id;
  }

  if (role === 'admin' && values.warehouse_id) {
    payload.order.warehouse_id = values.warehouse_id;
  }

  return payload;
}
